import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import {
  Building2, Users, BedDouble, IndianRupee, Activity, AlertTriangle,
  TrendingUp, ClipboardList, RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Platform Health — cross-tenant analytics for Super Admin only.
 * Cards: hospitals · users · beds occupancy · ops counters.
 * Table: top revenue tenants this month.
 * Snapshot table: every hospital with users/beds/occupancy.
 * Live audit feed.
 */
export default function PlatformHealthPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/platform/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load platform health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (!user?.is_super_admin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="platform-health-restricted">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Access Restricted</CardTitle>
                <CardDescription>Platform Health is reserved for the Platform Super Admin.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
              <a href="/dashboard">Go to Dashboard</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (err) return (
    <div className="p-6 text-red-600" data-testid="platform-health-error">Error: {err}</div>
  );

  const formatINR = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <div className="space-y-8" data-testid="platform-health-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Platform Health
          </h1>
          <p className="mt-1 text-slate-600">Cross-tenant analytics for the Klick Niramaya HMS platform.</p>
          <p className="mt-1 text-xs text-slate-400">As of {format(new Date(data.as_of), 'PPpp')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="platform-health-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-hospitals" icon={Building2} color="indigo"
          label="Hospitals" value={data.hospitals.total}
          sub={`${data.hospitals.active} active · ${data.hospitals.inactive} inactive`}
        />
        <StatCard testid="stat-users" icon={Users} color="emerald"
          label="Users" value={data.users.total}
          sub={`${data.users.active} active · ${data.users.super_admins} super`}
        />
        <StatCard testid="stat-occupancy" icon={BedDouble} color="amber"
          label="Bed Occupancy" value={`${data.beds.occupancy_pct}%`}
          sub={`${data.beds.occupied} / ${data.beds.total} occupied`}
        />
        <StatCard testid="stat-appts-today" icon={Activity} color="rose"
          label="Appointments Today" value={data.operations.appointments_today}
          sub={`${data.operations.patients_total.toLocaleString('en-IN')} patients · ${data.operations.appointments_total.toLocaleString('en-IN')} appts`}
        />
      </div>

      {/* Top revenue */}
      <Card data-testid="top-revenue-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle>Top Revenue This Month</CardTitle>
              <CardDescription>Top 5 tenants by invoiced amount in the current calendar month.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data.top_revenue_this_month.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No invoices recorded yet this month.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">Hospital</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600">Revenue (₹)</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-600">Invoices</th>
                </tr>
              </thead>
              <tbody>
                {data.top_revenue_this_month.map((r, i) => (
                  <tr key={r.hospital_id} className="border-b border-slate-100" data-testid={`top-revenue-row-${i}`}>
                    <td className="px-4 py-2.5 text-slate-700">{r.hospital_name}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-700 inline-flex items-center justify-end gap-1 w-full">
                      <IndianRupee className="h-3.5 w-3.5" /> {formatINR(r.revenue_inr)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{r.invoice_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Hospitals snapshot */}
      <Card data-testid="hospitals-snapshot-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Building2 className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle>Hospitals Snapshot</CardTitle>
              <CardDescription>Every tenant on Klick Niramaya HMS, ranked by active users.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-slate-600">Hospital</th>
                <th className="text-center px-4 py-2 font-semibold text-slate-600">Status</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-600">Users (active)</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-600">Beds (occ/total)</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-600">Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {data.snapshots.map((h) => (
                <tr key={h.hospital_id} className="border-b border-slate-100" data-testid={`snapshot-row-${h.slug}`}>
                  <td className="px-4 py-2.5 text-slate-700">
                    <div className="font-medium">{h.name}</div>
                    <div className="text-xs text-slate-400">/{h.slug}</div>
                  </td>
                  <td className="text-center px-4 py-2.5">
                    {h.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                      : <Badge variant="outline" className="bg-slate-100 text-slate-500">Inactive</Badge>
                    }
                  </td>
                  <td className="text-right px-4 py-2.5 text-slate-700">
                    {h.users_active} <span className="text-slate-400 text-xs">/ {h.users_total}</span>
                  </td>
                  <td className="text-right px-4 py-2.5 text-slate-700">
                    {h.beds_occupied} <span className="text-slate-400 text-xs">/ {h.beds_total}</span>
                  </td>
                  <td className="text-right px-4 py-2.5">
                    <span className={`font-semibold ${h.occupancy_pct >= 80 ? 'text-rose-600' : h.occupancy_pct >= 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {h.occupancy_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Recent audit */}
      <Card data-testid="recent-audit-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <ClipboardList className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <CardTitle>Recent Platform Activity</CardTitle>
              <CardDescription>Most recent 20 high-signal audit events across all tenants.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data.recent_audit.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No audit events yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_audit.map((a, i) => (
                <li key={i} className="py-2 text-sm flex flex-wrap items-center gap-2" data-testid={`audit-row-${i}`}>
                  <Badge variant="outline" className="text-xs">{a.action}</Badge>
                  <span className="text-slate-700 truncate max-w-[280px]">{a.user_email || '—'}</span>
                  {a.entity_type && (
                    <span className="text-slate-400 text-xs">· {a.entity_type}</span>
                  )}
                  {a.created_at && (
                    <span className="ml-auto text-xs text-slate-400">
                      {format(new Date(a.created_at), 'MMM d, HH:mm')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const StatCard = ({ testid, icon: Icon, color, label, value, sub }) => {
  const ring = {
    indigo: 'bg-indigo-100 text-indigo-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600',
  }[color] || 'bg-slate-100 text-slate-600';
  return (
    <Card className="card-hover" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg ${ring}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
